<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { formatIban, JwtParser, uuid } from '@/composables'
import { retrieveSetupIntentResult } from '@/composables/useStripe'
import { STATUS_CODES } from '@/constants'
import { onMounted, reactive, computed } from 'vue'
import { type JwtContent } from '@/types'
import { useJwtStore, useNotifyStore } from '@/stores'
import { DateTime } from 'luxon'
import sepaLogo from '@/assets/Single_Euro_Payments_Area_logo.svg'
import mastercardLogo from '@/assets/Mastercard_2019_logo.svg'
import visaLogo from '@/assets/Visa_Inc._logo.svg'
import paypalLogo from '@/assets/PayPal_logo.svg'
import { useAxios } from '@/composables/useAxios'
import axios from 'axios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import DashboardConfirmDialog from '@/components/dashboard/common/DashboardConfirmDialog.vue'
import StripeSetupModal from './StripeSetupModal.vue'

export type PaymentMethodType = 'bank' | 'sepa_debit' | 'mastercard' | 'visa' | 'credit_card' | 'amex' | 'paypal'

export type PaymentMethod = {
  account: string
  id: string
  revoked: boolean
  active_since: number
  type: PaymentMethodType
  default?: boolean
}

// Payment type helpers
const BANK_TYPES: PaymentMethodType[] = ['bank', 'sepa_debit']

const isBankPayment = (type: PaymentMethodType) => BANK_TYPES.includes(type)
// PayPal identifies the account by the payer's email rather than by a holder
// name and a masked number, so it gets its own single-row detail layout.
const isPayPalPayment = (type: PaymentMethodType) => type === 'paypal'

// Query parameters Stripe appends to the return_url after a redirect-based
// confirmation; stripped once the setup has been picked up.
const STRIPE_RETURN_PARAMS = ['setup_intent', 'setup_intent_client_secret', 'redirect_status']

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const notifyStore = useNotifyStore()

const data = reactive({
  loading: true,
  paymentMethods: [] as PaymentMethod[],
  jwt: {} as JwtContent
})

// Setup modal state
const setupModal = reactive({
  open: false,
  clientSecret: '',
  loading: false
})

// Revoke payment state
const revokeState = reactive({
  showDialog: false,
  paymentToRevoke: null as PaymentMethod | null,
  loading: false
})

// Computed: count of active payment methods
const activePaymentCount = computed(() =>
  data.paymentMethods.filter(p => !p.revoked).length
)

// Can revoke a payment (only if there's more than one active)
const canRevokePayment = computed(() => activePaymentCount.value > 1)

// Open revoke confirmation dialog
const openRevokeDialog = (payment: PaymentMethod) => {
  revokeState.paymentToRevoke = payment
  revokeState.showDialog = true
}

// Revoke payment method
const revokePayment = async () => {
  if (!revokeState.paymentToRevoke) return

  revokeState.loading = true

  try {
    const res = await useAxios<{ id: string }, null>('DELETE', `/account/payment/${revokeState.paymentToRevoke.id}`, true)
    if (res && (res.status === STATUS_CODES.OK || res.status === STATUS_CODES.NO_CONTENT)) {
      getPaymentsList()
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'AccountPagePayments:revoke' })
    let message = getErrorMessage(error)
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      message = t('dashboard.account.payments.cannotDeleteLast')
    }
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message
    })
  } finally {
    revokeState.loading = false
    revokeState.showDialog = false
    revokeState.paymentToRevoke = null
  }
}

// Set payment method as default
const setDefaultPayment = async (paymentId: string) => {
  try {
    const res = await useAxios<null, null>('POST', `/account/payment/${paymentId}/default`, true)
    if (res && (res.status === STATUS_CODES.OK || res.status === STATUS_CODES.NO_CONTENT)) {
      getPaymentsList()
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.account.payments.defaultMethod'),
        message: t('dashboard.account.payments.setDefaultSuccess')
      })
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'AccountPagePayments:setDefault' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(error)
    })
  }
}

// Open add payment modal - fetches SetupIntent first
const openAddPaymentModal = async () => {
  setupModal.loading = true
  try {
    const res = await useAxios<null, { intent_id: string; client_secret: string }>(
      'POST', '/account/payment/setup-intent', true
    )
    if (res?.data?.client_secret) {
      setupModal.clientSecret = res.data.client_secret
      setupModal.open = true
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'AccountPagePayments:setupIntent' })
    const isPhoneRequired = axios.isAxiosError(error) && error.response?.status === 403 && error.response?.data?.error === 'phone_verification_required'
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: isPhoneRequired ? 'warning' : 'error',
      title: isPhoneRequired ? t('dashboard.account.payments.phoneRequiredTitle') : t('common.error'),
      message: isPhoneRequired ? t('dashboard.account.payments.phoneRequired') : getErrorMessage(error)
    })
  } finally {
    setupModal.loading = false
  }
}

// Handle successful payment method setup. Returns whether the method was
// persisted, which also tells the caller whether the list was refreshed.
const onSetupSuccess = async (paymentMethodId: string): Promise<boolean> => {
  try {
    await useAxios<{ payment_method_id: string }, null>(
      'POST', '/account/payment/method', true,
      { payment_method_id: paymentMethodId }
    )
    setupModal.open = false
    setupModal.clientSecret = ''
    getPaymentsList()
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('dashboard.account.payments.addedTitle'),
      message: t('dashboard.account.payments.addedMessage')
    })
    return true
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'AccountPagePayments:confirmMethod' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(error)
    })
    return false
  }
}

// Handle setup error
const onSetupError = (message: string) => {
  notifyStore.createNotify({
    id: uuid('v4'),
    alertType: 'error',
    title: t('common.error'),
    message
  })
}

const getPaymentsList = () => {
  data.loading = true
  useAxios<null, Array<PaymentMethod>>('GET', '/account/payments', true)
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK && res.data) {
        data.paymentMethods = Array.from(res.data)
      }
    })
    .catch((error) => {
      handleAxiosError(error, { logPrefix: 'AccountPagePayments' })
    })
    .finally(() => {
      data.loading = false
    })
}

const getPaymentTypeIcon = (type: string) => {
  switch (type) {
    case 'bank': return sepaLogo
    case 'mastercard': return mastercardLogo
    case 'visa': return visaLogo
    case 'paypal': return paypalLogo
    default: return null
  }
}

// Renders the stored identifier for display. Each method type carries a
// different kind of value, so this has to branch: an IBAN gets grouped, a card
// shows only its last four, and a PayPal payer email must be left verbatim.
const formatAccountLabel = (type: PaymentMethodType, account: string) => {
  if (isPayPalPayment(type)) return account
  if (isBankPayment(type)) return formatIban(account)
  return `•••• ${account}`
}

const getAccountHolderName = () => {
  if (data.jwt.type === 'person') {
    return `${data.jwt.name} ${data.jwt.surname}`
  }
  return data.jwt.name
}

// Pick up a payment method the customer authorised on a provider's site.
// PayPal always redirects away to confirm the billing agreement, so the
// confirmSetup promise in StripeSetupModal never resolves for it — the intent
// comes back as query parameters on the return_url instead, and this is the
// only place the method gets persisted for that flow.
// Returns true when it already refreshed the list, so the caller doesn't
// fetch it a second time.
const resumeRedirectedSetup = async (): Promise<boolean> => {
  const clientSecret = route.query.setup_intent_client_secret
  if (typeof clientSecret !== 'string' || !clientSecret) return false

  // Drop the Stripe parameters first so a reload can't replay the setup,
  // keeping any unrelated query the page was opened with.
  const query = Object.fromEntries(
    Object.entries(route.query).filter(
      ([key]) => !STRIPE_RETURN_PARAMS.includes(key)
    )
  )
  await router.replace({ query })

  const result = await retrieveSetupIntentResult(clientSecret)
  if (result.success && result.paymentMethodId) {
    // Only a persisted method refreshes the list; a failed POST must still
    // fall through to the caller's fetch or the page never leaves its
    // loading state.
    return await onSetupSuccess(result.paymentMethodId)
  }
  if (result.error) onSetupError(result.error)
  return false
}

onMounted(async () => {
  const jwtStore = useJwtStore()
  const jwt = jwtStore.getJwt
  if (jwt) data.jwt = JwtParser(jwt) ?? ({} as JwtContent)

  // Resuming is best-effort: a rejected navigation guard or a blocked Stripe
  // script must not stop the list from loading, or the page is stuck on its
  // spinner with neither a grid nor an empty state.
  let listAlreadyRefreshed = false
  try {
    listAlreadyRefreshed = await resumeRedirectedSetup()
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'AccountPagePayments:resumeSetup' })
  }
  if (!listAlreadyRefreshed) getPaymentsList()
})
</script>

<template>
  <div class="payments-page">
    <!-- Section Header -->
    <div class="section-header">
      <div class="section-info">
        <h2 class="section-title">{{ $t('dashboard.account.payments.title') }}</h2>
        <p class="section-description">{{ $t('dashboard.account.payments.description') }}</p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="data.loading" class="loading-state">
      <v-progress-circular indeterminate color="primary" size="40" />
      <p>{{ $t('dashboard.account.payments.loading') }}</p>
    </div>

    <!-- Payment Methods Grid -->
    <div v-else class="payment-methods-grid">
      <!-- Existing Payment Methods -->
      <div
        v-for="payment in data.paymentMethods"
        :key="payment.id"
        class="payment-card"
        :class="{ active: !payment.revoked }"
      >
        <div class="payment-header">
          <div class="payment-type">
            <img
              v-if="payment.type !== 'credit_card' && getPaymentTypeIcon(payment.type)"
              :src="getPaymentTypeIcon(payment.type)"
              :alt="payment.type"
              class="payment-logo"
            />
            <v-icon v-else icon="mdi-credit-card" size="24" />
          </div>
          <div class="payment-status">
            <span v-if="payment.default" class="status-badge status-default">
              <v-icon icon="mdi-star" size="14" />
              {{ $t('dashboard.account.payments.defaultMethod') }}
            </span>
            <span v-else-if="!payment.revoked" class="status-badge status-active">
              <v-icon icon="mdi-check-circle" size="14" />
              {{ $t('dashboard.account.payments.active') }}
            </span>
            <span v-else class="status-badge status-revoked">
              {{ $t('dashboard.account.payments.revoked') }}
            </span>
          </div>
        </div>

        <div class="payment-details">
          <div v-if="isPayPalPayment(payment.type)" class="detail-row">
            <span class="detail-label">{{ $t('dashboard.account.payments.paypalAccount') }}</span>
            <span class="detail-value account-number">{{ payment.account }}</span>
          </div>
          <template v-else>
            <div class="detail-row">
              <span class="detail-label">{{ isBankPayment(payment.type) ? $t('dashboard.account.payments.accountHolder') : $t('dashboard.account.payments.cardHolder') }}</span>
              <span class="detail-value">{{ getAccountHolderName() }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ isBankPayment(payment.type) ? $t('dashboard.account.payments.ibanLabel') : $t('dashboard.account.payments.cardNumber') }}</span>
              <span class="detail-value account-number">{{ formatAccountLabel(payment.type, payment.account) }}</span>
            </div>
          </template>
        </div>

        <div class="payment-footer">
          <span class="payment-date">
            {{ $t('dashboard.account.payments.since') }} {{ DateTime.fromSeconds(payment.active_since).toLocaleString(DateTime.DATE_MED) }}
          </span>
          <div class="payment-actions">
            <button
              v-if="!payment.revoked && !payment.default"
              type="button"
              class="btn-action btn-default"
              :title="$t('dashboard.account.payments.setDefault')"
              @click="setDefaultPayment(payment.id)"
            >
              <v-icon icon="mdi-star-outline" size="16" />
            </button>
            <button
              v-if="!payment.revoked"
              type="button"
              class="btn-action btn-revoke"
              :disabled="!canRevokePayment"
              :title="!canRevokePayment ? $t('dashboard.account.payments.cannotRevokeLastMethod') : $t('dashboard.account.payments.revokeMethod')"
              @click="openRevokeDialog(payment)"
            >
              <v-icon icon="mdi-credit-card-off" size="16" />
            </button>
          </div>
        </div>
      </div>

      <!-- Add New Payment Card -->
      <button class="payment-card add-card" :disabled="setupModal.loading" @click="openAddPaymentModal">
        <div class="add-icon">
          <v-progress-circular v-if="setupModal.loading" indeterminate size="32" width="2" />
          <v-icon v-else icon="mdi-plus" size="32" />
        </div>
        <span class="add-text">{{ $t('dashboard.account.payments.addMethod') }}</span>
      </button>
    </div>

    <!-- Empty State -->
    <div v-if="!data.loading && data.paymentMethods.length === 0" class="empty-state">
      <div class="empty-icon">
        <v-icon icon="mdi-credit-card-off-outline" size="48" />
      </div>
      <p class="empty-title">{{ $t('dashboard.account.payments.empty') }}</p>
      <p class="empty-description">{{ $t('dashboard.account.payments.emptyHint') }}</p>
    </div>

    <!-- Add Payment Modal (Stripe) -->
    <StripeSetupModal
      v-model="setupModal.open"
      :client-secret="setupModal.clientSecret"
      @success="onSetupSuccess"
      @error="onSetupError"
    />

    <!-- Revoke Confirmation Dialog -->
    <DashboardConfirmDialog
      v-model="revokeState.showDialog"
      :title="$t('dashboard.account.payments.revokeTitle')"
      :description="$t('dashboard.account.payments.revokeDescription')"
      variant="danger"
      :confirm-text="$t('dashboard.account.payments.revokeConfirm')"
      confirm-icon="mdi-credit-card-off"
      :loading="revokeState.loading"
      @confirm="revokePayment"
    >
      <div v-if="revokeState.paymentToRevoke" class="revoke-details">
        <div class="revoke-account">
          {{ formatAccountLabel(revokeState.paymentToRevoke.type, revokeState.paymentToRevoke.account) }}
        </div>
      </div>
    </DashboardConfirmDialog>
  </div>
</template>

<style lang="scss" scoped>
.payments-page {
  width: 100%;
}

/* Section Header */
.section-header {
  margin-bottom: 28px;
}

.section-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 4px;
}

.section-description {
  font-size: 0.875rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 60px 20px;
  color: rgba(200, 218, 255, 0.6);

  p {
    font-size: 0.95rem;
    margin: 0;
  }
}

/* Payment Methods Grid */
.payment-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

/* Payment Card */
.payment-card {
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 16px;
  transition: all 0.2s ease;

  &.active {
    border-color: rgba(76, 175, 80, 0.3);
    box-shadow: 0 0 20px rgba(76, 175, 80, 0.1);
  }
}

.payment-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.payment-type {
  display: flex;
  align-items: center;
}

.payment-logo {
  height: 24px;
  width: auto;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-active {
  background: rgba(76, 175, 80, 0.1);
  color: #66bb6a;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.status-revoked {
  background: rgba(158, 158, 158, 0.1);
  color: rgba(200, 218, 255, 0.5);
  border: 1px solid rgba(158, 158, 158, 0.2);
}

.status-default {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
  border: 1px solid rgba(255, 193, 7, 0.2);
}

.payment-details {
  flex: 1;
  margin-bottom: 16px;
}

.detail-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.detail-label {
  font-size: 0.7rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.detail-value {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
}

.account-number {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.7);
  letter-spacing: 0.05em;
}

.payment-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid rgba(120, 163, 255, 0.08);
}

.payment-date {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.4);
}

.payment-actions {
  display: flex;
  gap: 8px;
}

.btn-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.btn-default {
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.2);
  color: #ffc107;

  &:hover {
    background: rgba(255, 193, 7, 0.2);
    border-color: rgba(255, 193, 7, 0.4);
  }
}

.btn-revoke {
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.2);
  color: #ef5350;

  &:hover:not(:disabled) {
    background: rgba(244, 67, 54, 0.2);
    border-color: rgba(244, 67, 54, 0.4);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

/* Add Card */
.add-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 180px;
  background: rgba(255, 255, 255, 0.02);
  border: 2px dashed rgba(120, 163, 255, 0.15);
  cursor: pointer;

  &:hover {
    background: rgba(0, 176, 255, 0.05);
    border-color: rgba(0, 176, 255, 0.3);

    .add-icon {
      background: rgba(0, 176, 255, 0.15);
      color: #00B0FF;
    }
  }
}

.add-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  color: rgba(200, 218, 255, 0.5);
  transition: all 0.2s ease;
}

.add-text {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 20px;
  color: rgba(200, 218, 255, 0.3);
  margin-bottom: 20px;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 8px;
}

.empty-description {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
  max-width: 300px;
}

/* Revoke Dialog */
.revoke-details {
  padding: 16px;
  background: rgba(244, 67, 54, 0.05);
  border: 1px solid rgba(244, 67, 54, 0.15);
  border-radius: 10px;
}

.revoke-account {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  letter-spacing: 0.05em;
}

/* Light Theme */
.v-theme--lightTheme {
  .section-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .section-description,
  .loading-state,
  .add-text,
  .payment-date {
    color: rgba(15, 23, 42, 0.5);
  }

  .payment-card {
    background: rgba(255, 255, 255, 0.5);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .account-holder {
    color: rgba(15, 23, 42, 0.9);
  }

  .account-number {
    color: rgba(15, 23, 42, 0.6);
  }

  .add-card {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(28, 110, 227, 0.15);

    &:hover {
      background: rgba(28, 110, 227, 0.05);
      border-color: rgba(28, 110, 227, 0.3);

      .add-icon {
        background: rgba(28, 110, 227, 0.1);
        color: #1c6ee3;
      }
    }
  }

  .add-icon {
    background: rgba(0, 0, 0, 0.05);
    color: rgba(15, 23, 42, 0.4);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .payment-methods-grid {
    grid-template-columns: 1fr;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .payment-card,
  .add-icon {
    transition: none;
  }
}
</style>
